<script setup lang="ts">
import type { Component } from 'vue'

/**
 * An underline tab strip: one link per tab, the active one gets the accent underline.
 * Routing-agnostic - `as` renders each tab (e.g. RouterLink) and the caller decides `active`,
 * the same way ActionButton's `as` keeps this package out of the router. `as="button"` (`to`
 * left unset per tab) switches a tab locally instead of navigating - the caller listens for
 * `select` and sets its own `active` state; a route-based caller can simply ignore it.
 */
withDefaults(
  defineProps<{
    tabs: readonly { to?: unknown; label: string; active: boolean }[]
    ariaLabel: string
    as?: string | Component
  }>(),
  { as: 'a' },
)
const emit = defineEmits<{ select: [index: number] }>()
</script>

<template>
  <nav :aria-label="ariaLabel" class="flex min-w-0 gap-[var(--space-inline)] overflow-x-auto">
    <component
      :is="as"
      v-for="(tab, index) in tabs"
      :key="tab.label"
      :to="tab.to"
      class="flex min-h-[var(--density-target-min)] shrink-0 items-center px-[var(--space-cell)] py-[var(--space-inline)] text-[length:var(--text-step-3)] font-medium"
      :class="
        tab.active
          ? 'border-b-2 border-[color:var(--color-accent)] text-[color:var(--color-text)]'
          : 'text-[color:var(--color-text-muted)]'
      "
      :aria-current="tab.active ? 'page' : undefined"
      @click="emit('select', index)"
    >
      {{ tab.label }}
    </component>
  </nav>
</template>
